import { useEffect, useState } from "react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { DatePicker } from "@/components/ui/date-picker";
import { TimePicker } from "@/components/ui/time-picker";
import { formatDuration } from "@/lib/api";
import { toDateStr, toTimeStr, parseDateTime } from "./utils";

/**
 * Manual-entry popover anchored on the TimerBar timer-counter button. Lets the
 * user record a past time entry without starting/stopping the live timer.
 */
export default function ManualEntryPopover({
  open, onOpenChange, description, clientIds, activityId, onSave, children,
}) {
  const now = new Date();
  const [date, setDate] = useState(toDateStr(now));
  const [startStr, setStartStr] = useState(toTimeStr(now));
  const [endStr, setEndStr] = useState(toTimeStr(now));

  useEffect(() => {
    if (open) {
      const d = new Date();
      setDate(toDateStr(d));
      setStartStr(toTimeStr(d));
      setEndStr(toTimeStr(d));
    }
  }, [open]);

  const startDt = parseDateTime(date, startStr);
  const endDt = parseDateTime(date, endStr);
  const durationSec = Math.max(0, Math.floor((endDt - startDt) / 1000));
  const needsClient = !clientIds || clientIds.length === 0;

  const setDurationFromText = (txt) => {
    const trimmed = txt.trim();
    let totalMin = 0;
    if (/^\d+:\d{1,2}(:\d{1,2})?$/.test(trimmed)) {
      const parts = trimmed.split(":").map(Number);
      const h = parts[0] || 0;
      const m = parts[1] || 0;
      const s = parts[2] || 0;
      totalMin = h * 60 + m + s / 60;
    } else {
      const hMatch = /(\d+)\s*h/i.exec(trimmed);
      const mMatch = /(\d+)\s*m/i.exec(trimmed);
      if (hMatch) totalMin += Number(hMatch[1]) * 60;
      if (mMatch) totalMin += Number(mMatch[1]);
      if (!hMatch && !mMatch && /^\d+$/.test(trimmed)) totalMin = Number(trimmed);
    }
    if (totalMin <= 0) return;
    const newEnd = new Date(startDt.getTime() + totalMin * 60 * 1000);
    setEndStr(toTimeStr(newEnd));
  };

  const handleSave = () => {
    if (durationSec <= 0 || needsClient) return;
    onSave({
      description,
      client_ids: clientIds,
      activity_id: activityId,
      start: startDt.toISOString(),
      end: endDt.toISOString(),
    });
    onOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="end" data-testid="manual-entry-popover">
        <div className="p-4 border-b border-slate-100 grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Start</div>
            <TimePicker
              value={startStr}
              onChange={setStartStr}
              className="w-full"
              testId="manual-start-input"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Stop</div>
            <TimePicker
              value={endStr}
              onChange={setEndStr}
              className="w-full"
              testId="manual-end-input"
            />
          </div>
        </div>

        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Duration</div>
            <button
              onClick={() => setDate(toDateStr(new Date()))}
              className="text-xs text-[#145c8c] hover:underline"
              data-testid="manual-today-button"
            >
              Today
            </button>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              defaultValue={formatDuration(durationSec)}
              onBlur={(e) => setDurationFromText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              placeholder="0:30:00 or 1h 30m"
              className="timer-font text-xl tracking-wider w-32 border border-slate-200 rounded-md px-2 py-1.5 focus:border-[#145c8c] outline-none"
              data-testid="manual-duration-input"
            />
            <DatePicker
              value={date}
              onChange={setDate}
              className="flex-1"
              testId="manual-date-input"
            />
          </div>
        </div>

        {needsClient && (
          <div className="px-4 py-2 text-xs text-amber-600 bg-amber-50 border-b border-amber-100"
               data-testid="manual-client-required">
            Pick a client in the timer bar before adding a manual entry.
          </div>
        )}

        <div className="p-3 flex items-center justify-end gap-2">
          <button
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-900"
            data-testid="manual-cancel-button"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={durationSec <= 0 || needsClient}
            className="px-4 py-1.5 text-sm text-white rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "#145c8c" }}
            title={needsClient ? "Pick a client to enable" : ""}
            data-testid="manual-save-button"
          >
            Add entry
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
